import { useMemo } from "react";

import cn from "@utils/cn";
import { interpolate } from "@utils";
import { useKinndomContext } from "@contexts/KinndomContext";

import NavbarSlot from "@components/navbar/NavbarSlot";
import FavoritesNavbarMenu from "@components/navbar/FavoritesNavbarMenu";

const NAVBAR_EMOJI = process.env.NODE_ENV === "production" ? "💠" : "🌿";

const CLASS_NAMES = cn(`
  hidden
  md:flex
  fixed
  inset-x-0
  top-0
  z-50
  max-h-navbar-height
  px-8
  overflow-y-unset

  bg-(--navbar-background-color)
  text-(--navbar-foreground-color)
  shadow-navbar
`);

// TODO: Extract out into separate component
const NavbarQuickLinks = (props) => {
  const {
    unreadNotificationsCount,
    incompleteAssignedTasksCount,
  } = props;

  const hasUnreadNotifications = unreadNotificationsCount > 0;
  const hasIncompleteAssignedTasks = incompleteAssignedTasksCount > 0;

  const items = [
    {
      label: {
        icon: {
          name: "hexagon-check",
          color: hasIncompleteAssignedTasks ? "#FFCB00" : "#FFFFFF",
        },
        text: hasIncompleteAssignedTasks ? incompleteAssignedTasksCount : null,
      },
      path: "/tasks",
      tooltip: "Tasks",
    },
    {
      label: {
        icon: {
          name: hasUnreadNotifications ? "bell-exclamation" : "bell",
          color: hasUnreadNotifications ? "#FFCB00" : "#FFFFFF",
        },
        text: hasUnreadNotifications ? unreadNotificationsCount : null,
      },
      path: "/notifications",
      tooltip: "Notifications",
    },
  ];

  return items.map((item, index) => (
    <NavbarSlot
      key={index}
      type="link"
      label={item.label}
      url={item.url}
      path={item.path}
      openInNewWindow={item.openInNewWindow}
      tooltip={item.tooltip}
    />
  ));
};

// TODO: Extract out into separate component
const UserNavbarMenu = (props) => {
  const {
    userName,
    userAvatarUrl,
    latestChangelogVisited,
  } = props;

  const label = {
    text: userName || "User",
    imageUrl: userAvatarUrl,
  };

  const flags = useMemo(() => {
    const flags = [];

    if (!latestChangelogVisited) {
      flags.push({
        icon: "circle-small",
        color: "#CF2828",
      });
    }

    return flags;
  }, [latestChangelogVisited]);

  const items = [
    {
      type: "link",
      label: {
        text: "Profile",
        icon: "user",
      },
      path: "/profile",
    },
    {
      type: "link",
      label: {
        text: "Log Out",
        icon: "right-from-bracket",
      },
      path: "/logout",
    },
    {
      type: "divider",
    },
    {
      type: "link",
      label: {
        text: `v${window.KINN_APP_VERSION}`,
      },
      tooltip: window.KINN_APP_VERSION_DATE || null,
      className: "font-mono font-bold text-sm bg-steel-50 text-steel-700",
      path: "/changelog",
      flags: flags,
    },
  ];

  return (
    <NavbarSlot
      type="menu"
      label={label}
      items={items}
      isAlignedRight={true}
      menuClassName="min-w-48"
    />
  );
};

export default function Navbar(_props) {
  const kinndom = useKinndomContext();

  const {
    kinndomKey,
    defaultWebsiteId,
    defaultWebsiteBaseUrl,
    isAuthenticated,

    currentUserName,
    isCurrentUserAdmin,
    currentUserHomepageOverride,
    currentUserAvatarUrl,
    currentUserLatestChangelogVisited,
    currentUserUnreadNotificationsCount,
    currentUserIncompleteAssignedTasksCount,
  } = window.appContext;

  // TODO: Refactor
  const interpolationData = useMemo(() => ({
    kinndomKey: kinndomKey,
    defaultWebsiteId: defaultWebsiteId,
    defaultWebsiteBaseUrl: defaultWebsiteBaseUrl,

    kinndom: kinndom,
    config: kinndom.configuration,
  }), [kinndomKey, defaultWebsiteId, defaultWebsiteBaseUrl, kinndom]);

  const navbarMenus = useMemo(() => {
    return (window.NAVBAR_MENUS || [])
      .filter((menu) => (
        !menu.isHidden
        && (!menu.isAdminOnly || isCurrentUserAdmin)
      )).map((menu) => interpolate(menu, interpolationData));
  }, [isCurrentUserAdmin, interpolationData]);

  const actionsNavbarMenu = navbarMenus.at(-1);
  const standardNavbarMenus = navbarMenus.slice(0, -1);

  const DEFAULT_HOMEPAGE_URL = "/";
  const homepageUrl = currentUserHomepageOverride || DEFAULT_HOMEPAGE_URL;

  /**************************************************************************************************/

  if (!isAuthenticated) return null;

  return (
    <nav className={CLASS_NAMES}>
      <div className="container flex flex-row max-w-(--breakpoint-2xl) px-4 mx-auto">
        <div className="flex flex-row w-full justify-between">
          <div className="flex flex-row">
            <NavbarSlot
              type="link"
              label={{
                text: NAVBAR_EMOJI,
              }}
              url={homepageUrl}
            />

            {standardNavbarMenus.map((menu, index) => (
              <NavbarSlot
                key={index}
                type="menu"
                {...menu}
              />
            ))}

            <FavoritesNavbarMenu />

            {actionsNavbarMenu && (
              <NavbarSlot
                type="menu"
                {...actionsNavbarMenu}
              />
            )}
          </div>

          <div className="flex items-center">
            <NavbarQuickLinks
              unreadNotificationsCount={currentUserUnreadNotificationsCount}
              incompleteAssignedTasksCount={currentUserIncompleteAssignedTasksCount}
            />
            <UserNavbarMenu
              userName={currentUserName}
              userAvatarUrl={currentUserAvatarUrl}
              latestChangelogVisited={currentUserLatestChangelogVisited}
            />
          </div>
        </div>
      </div>
    </nav>
  );
}
